// built-from: /goal-outcome-measure-overview v1 impl:goal-outcome-measure-overview role:implementation
import { Link } from 'react-router-dom';
import type { LumenGridModernisationJourney } from '../fixtures/modernisation/scenarios';
import type { Engagement } from '../hooks/useEngagement';

type Props = {
  journey: LumenGridModernisationJourney;
  compact?: boolean;
  engagement?: Engagement | null;
};

type RealGoal = { id: string; text: string };

function realDirection(engagement: Engagement): { headline: string | null; goals: RealGoal[] } {
  const vision = engagement.vision as {
    goals?: Array<{ id?: unknown; text?: unknown }>;
    coverStory?: { headline?: unknown } | null;
  } | null;
  const headline = typeof vision?.coverStory?.headline === 'string'
    && vision.coverStory.headline.trim()
    ? vision.coverStory.headline.trim()
    : null;
  const goals = Array.isArray(vision?.goals)
    ? vision.goals.flatMap((goal, index) => (
      typeof goal?.text === 'string' && goal.text.trim()
        ? [{ id: typeof goal.id === 'string' ? goal.id : `goal-${index + 1}`, text: goal.text.trim() }]
        : []
    ))
    : [];
  return { headline, goals };
}

function RealDirectionOverview({ engagement }: { engagement: Engagement }): JSX.Element {
  const direction = realDirection(engagement);
  return (
    <div className="journey-page">
      <header className="journey-page__header">
        <span className="journey-eyebrow">Engagement direction</span>
        <h1>{direction.headline || 'Direction not yet captured'}</h1>
        <p>{engagement.client} · {engagement.programme}</p>
      </header>

      <div className="journey-support-grid">
        <section className="journey-panel" aria-labelledby="outcomes-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Agreed direction</span>
              <h2 id="outcomes-title">Desired outcomes</h2>
            </div>
            <span className="journey-count">{direction.goals.length}</span>
          </div>
          {direction.goals.length ? (
            <ul className="journey-check-list">
              {direction.goals.map((goal) => <li key={goal.id}>{goal.text}</li>)}
            </ul>
          ) : (
            <div className="journey-empty-state" role="status">
              <strong>No desired outcomes have been captured.</strong>
              <span>Use the Vision activity to agree this engagement's direction.</span>
            </div>
          )}
        </section>

        <section className="journey-panel" aria-labelledby="measures-title">
          <div className="journey-section-heading journey-section-heading--compact">
            <div>
              <span className="journey-eyebrow">Evidence of progress</span>
              <h2 id="measures-title">Measures</h2>
            </div>
            <span className="journey-count">0</span>
          </div>
          <div className="journey-empty-state" role="status">
            <strong>No measures have been captured.</strong>
            <span>Measures will appear here when they are recorded for this engagement.</span>
          </div>
        </section>
      </div>
    </div>
  );
}

export function GoalOutcomeMeasureOverview({ journey, compact = false, engagement }: Props): JSX.Element {
  if (engagement) return <RealDirectionOverview engagement={engagement} />;

  if (compact) {
    return (
      <section className="prototype-card prototype-direction" aria-labelledby="direction-title">
        <div className="prototype-section-heading">
          <div>
            <span className="prototype-eyebrow">Engagement direction</span>
            <h2 id="direction-title">What LumenGrid is trying to achieve</h2>
          </div>
          <Link className="prototype-text-link" to="/engagement/goals">View goals &amp; measures</Link>
        </div>
        <p className="prototype-goal-statement">{journey.goal.statement}</p>
        <div className="prototype-measure-strip" aria-label="Direction status">
          <span><strong>{journey.outcomes.length}</strong> outcomes agreed</span>
          <span><strong>{journey.measures.length}</strong> measures defined</span>
          <span className="prototype-status prototype-status--attention">2 baselines needed</span>
        </div>
      </section>
    );
  }

  return (
    <div className="prototype-page">
      <header className="prototype-page-header">
        <span className="prototype-eyebrow">Goals &amp; measures</span>
        <h1>{journey.goal.name}</h1>
        <p>{journey.goal.statement}</p>
      </header>

      <section className="prototype-grid prototype-grid--two" aria-label="Agreed direction">
        <div className="prototype-card">
          <div className="prototype-section-heading">
            <h2>Desired outcomes</h2>
            <span className="prototype-status prototype-status--good">Agreed</span>
          </div>
          <div className="prototype-list">
            {journey.outcomes.map((outcome) => (
              <div className="prototype-list-row" key={outcome.id}>
                <span className="prototype-list-marker" aria-hidden="true">✓</span>
                <div><strong>{outcome.name}</strong><p>{outcome.statement}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div className="prototype-card">
          <div className="prototype-section-heading">
            <h2>Measures</h2>
            <span className="prototype-status prototype-status--attention">Needs input</span>
          </div>
          <div className="prototype-list">
            {journey.measures.map((measure) => (
              <div className="prototype-measure" key={measure.id}>
                <strong>{measure.name}</strong>
                <dl><dt>Current</dt><dd>{measure.baseline}</dd><dt>Target</dt><dd>{measure.target}</dd></dl>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
